import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Hangul from 'hangul-js';

import './ListOfEmployees.scss';
import SearchBox from './searchBox/SearchBox';
import SelectionBox from './selectionBox/SelectionBox';
import { IEmpInfoData } from 'types/listOfEmployees';
import moment from 'moment';
import { phoneNumberAddHypen } from 'constants/utils';
import { IOption } from 'types/common';
import { positionTypeOptions, payTypeOptions, calendarTypeOptions } from 'constants/dataArr';
import { SingleValue } from 'react-select';
import useAlertConfirmStore from 'zustands/useAlertConfirmStore';
import { AxiosResponse } from 'axios';
import useApi from 'hooks/useApi';

interface Props {
  empListData: IEmpInfoData[];
  fetchEmpData: () => void;
}
interface ISelectUser {
  id: number;
  name: string;
}
const ListOfNotWorkingEmployeesTable = ({ empListData, fetchEmpData }: Props) => {
  const navigate = useNavigate();
  const { webApiNode } = useApi();

  const { setModalState } = useAlertConfirmStore();
  const [selectIds, setSelectIds] = React.useState<number[]>([]);
  const [selectedOptions, setSelectedOptions] = React.useState({
    position: { value: '전체', label: '전체' },
    payType: { value: '전체', label: '전체' },
    calendar: { value: '전체', label: '전체' },
  });
  const [searchValue, setSearchValue] = React.useState<string>('');

  // 필터링 셀렉터 상태 데이터 핸들링
  const handleFilterSelect = (value: SingleValue<IOption>, name: string) => {
    setSelectedOptions((prev) => ({ ...prev, [name]: value }));
  };

  // 검색어 인풋 입력 상태 핸들
  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  // 상세정보 화면 이동
  const enterDetailInfo = (empData: IEmpInfoData) => {
    navigate('/view-information', {
      state: {
        empData,
      },
    });
  };

  const tableHeaders = ['상태', '근무일정', '직책', '급여유형', '근무유형', '휴대폰번호', '더보기'];
  const customCalendarOptions = [{ value: '전체', label: '전체' }, ...calendarTypeOptions];
  const customPositionOptions = [{ value: '전체', label: '전체' }, ...positionTypeOptions];
  const customPayTypeOptions = [{ value: '전체', label: '전체' }, ...payTypeOptions];
  const filteredEmpListData = empListData
    .filter((i) => {
      return i.END !== null || (i.END !== null && moment(i.END).isAfter(moment()));
    })
    .filter((i) => {
      if (selectedOptions.position.label !== '전체') {
        return selectedOptions.position.value === i.IS_MANAGER;
      }
      return i;
    })
    .filter((i) => {
      if (selectedOptions.payType.label !== '전체') {
        return selectedOptions.payType.value === i.PAY_TYPE;
      }
      return i;
    })
    .filter((i) => {
      if (selectedOptions.calendar.label !== '전체') {
        return selectedOptions.calendar.value === i.CALENDAR;
      }
      return i;
    });

  // 데이터 검색어 필터링
  const searchFilteredEmpListData = () => {
    const list = filteredEmpListData;
    if (list.length > 0) {
      const search1 = searchValue.replace(/-/g, '');
      const search2 = Hangul.disassemble(search1).join('');
      const result = list.filter((item) => {
        return (
          item.EMP_NAME?.includes(search1) || item.MobileNo?.includes(search1) || item.disassembled?.includes(search2)
        );
      });

      return searchValue === '' ? filteredEmpListData : result;
    }
    return [];
  };

  const onChangeCheckbox = (id: number) => {
    if (selectIds.includes(id)) {
      setSelectIds(selectIds.filter((selectId) => selectId !== id));
    } else {
      setSelectIds([...selectIds, id]);
    }
  };

  const onChangeAllCheckbox = (checked: boolean, ids: number[]) => {
    if (checked) {
      setSelectIds(ids);
    } else {
      setSelectIds([]);
    }
  };

  const deleteMember = async () => {
    try {
      // DELETE MEMBER API
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/delEmps`, {
        EMP_LIST: selectIds,
      });
    } catch (err) {
      console.error(err);
    } finally {
      fetchEmpData();
    }
  };

  // searchFilteredEmpListData()
  //   .filter((item) => selectIds.includes(item.MEMBER_SEQ))
  //   .map((item) => item.EMP_NAME);

  return (
    <div className="listOfEmployeesScreen">
      <div className="header">
        <div className="notWorkingLeftContainer">퇴직 직원목록</div>
      </div>
      <div className="detailCard">
        <div className="cardHeader">
          <div className="leftContainer">
            총
            {
              empListData.filter((i) => {
                return i.END !== null || (i.END !== null && moment(i.END).isAfter(moment()));
              }).length
            }
            명
          </div>
          <div className="rightContainer">
            <SelectionBox
              label="직책"
              name="position"
              value={selectedOptions.position}
              options={customPositionOptions}
              onChange={handleFilterSelect}
            />
            <SelectionBox
              label="급여유형"
              name="payType"
              value={selectedOptions.payType}
              options={customPayTypeOptions}
              onChange={handleFilterSelect}
            />
            <SelectionBox
              label="근무유형"
              name="calendar"
              value={selectedOptions.calendar}
              options={customCalendarOptions}
              onChange={handleFilterSelect}
            />
            <SearchBox
              value={searchValue}
              name={'searchValue'}
              onChange={handleSearchInput}
              placeHolder="이름 또는 휴대폰번호 검색"
              maxLength={20}
            />
          </div>
        </div>

        <div className="deleteContainer">
          <button
            className="delete"
            onClick={() => {
              if (selectIds.length === 0) {
                alert('삭제하려는 멤버를 선택해주세요.');
              } else {
                setModalState({
                  isModalShow: true,
                  title: '직원 삭제',
                  desc: `${selectIds.length}명 직원을 삭제하시겠습니까?`,
                  okBtnText: '확인',
                  okCallback: deleteMember,
                  isWarning: true,
                });
              }
            }}>
            멤버 삭제
          </button>
        </div>
        <div className="tableContainer">
          <table>
            <tr>
              <th>
                <div className="flexibleColumnsNotWorking">
                  <input
                    type={'checkbox'}
                    className="checkboxInput"
                    onChange={(e) => {
                      onChangeAllCheckbox(
                        e.target.checked,
                        searchFilteredEmpListData().map((a) => a.EMP_SEQ),
                      );
                    }}
                  />
                  이름
                </div>
              </th>

              {tableHeaders.map((header) => (
                <th key={header}>{header}</th>
              ))}
            </tr>

            {empListData.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', color: '#ccc' }}>
                  합류된 직원이 없습니다
                </td>
              </tr>
            ) : searchFilteredEmpListData().length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', color: '#ccc' }}>
                  해당 직원이 없습니다
                </td>
              </tr>
            ) : (
              searchFilteredEmpListData().map((i) => {
                const isWorking = i.END === null || (i.END !== null && moment(i.END).isAfter(moment()));
                return (
                  <tr key={i.EMP_SEQ}>
                    <td>
                      <div className="flexibleColumnsNotWorking">
                        <input
                          type="checkbox"
                          checked={selectIds.includes(i.EMP_SEQ) ? true : false}
                          onChange={() => {
                            onChangeCheckbox(i.EMP_SEQ);
                          }}
                        />
                        <div className="nameCircle">{i.EMP_NAME.substring(1).substr(0, 2)}</div>
                        <div className="nameText">{i.EMP_NAME}</div>
                      </div>
                    </td>
                    <td>
                      <div className="stateCellContainer">
                        <div
                          className="stateIndicator"
                          style={{
                            backgroundColor: isWorking ? '#E47C77' : ' #49AE9E',
                          }}
                        />
                        {isWorking ? '재직' : '퇴직'}
                        {i.probation === '1' ? (
                          moment(i.probationDATE).isBefore(moment()) ? (
                            <div className="probation">수습완료</div>
                          ) : (
                            <div className="probation">수습중</div>
                          )
                        ) : null}
                      </div>
                    </td>
                    <td>일정 없음</td>
                    <td>{i.IS_MANAGER === '1' ? '매니저' : '스태프'}</td>
                    <td>{i.PAY_TYPE === '0' ? '시급' : i.PAY_TYPE === '1' ? '일급' : '월급'}</td>
                    <td>
                      <div className={`typeContainer ${i.CALENDAR === '1' ? ' redType' : ' blueType'}`}>
                        {i.CALENDAR === '0' ? '고정근무' : '자율출퇴근'}
                      </div>
                    </td>
                    <td>{phoneNumberAddHypen(i.MobileNo)}</td>
                    <td>
                      {i.MEM_MEMBER_SEQ ? (
                        <button className="actionButton" onClick={() => enterDetailInfo(i)}>
                          정보수정
                        </button>
                      ) : (
                        <button className="disabledButton" disabled>
                          탈퇴직원
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default ListOfNotWorkingEmployeesTable;
